import type {
  UseQueryOptions,
  UseQueryResult,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';

import { useQuery as useReactQuery } from '@tanstack/react-query';
import React from 'react';

import type { BaseApiErrorResponse } from '../../api/types/api.types';

// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
type NonFunctionGuard<T> = T extends Function ? never : T;

/**
 * Serves as base query for fetching data
 */
export default function useQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  /**
   * Options for query
   */
  options: UseQueryOptions<
    TQueryFnData,
    BaseApiErrorResponse<TError | null>,
    TData,
    TQueryKey
  >,
  /**
   * queryClient if required. Otherwise it defaults to query client found from ApiClientProvider
   */
  queryClient?: QueryClient,
): UseQueryResult<TData, BaseApiErrorResponse<TError | null>> {
  // The previous key's data stays on screen by DEFAULT — and only by default:
  // the caller's options come after it, so a scoped query can pass its own
  // placeholder or an explicit `placeholderData: undefined` (an own key whose
  // value is undefined still wins the spread).
  const memoizedArgs = React.useMemo(
    () => ({
      placeholderData: (previous: NonFunctionGuard<TQueryFnData> | undefined) =>
        previous,
      ...options,
    }),
    [options],
  );
  // Returned AS IS. React Query hands back a tracked object whose properties
  // are getters that subscribe the caller to just the fields it reads;
  // destructuring and rebuilding it here read all of them (and collapsed the
  // result union, which is what the old `@ts-ignore` was hiding).
  return useReactQuery(memoizedArgs, queryClient);
}
